"use strict";
var Promise = require("promise");
var flyd = require("flyd");
var filter = require("flyd/module/filter");
var par = require("par");
var pipe = require("fj-pipe");
var values = require("object-values");
var window = require("global");

var CONSTANTS = require("./constants");

// Temporary hack to delay listening
var LISTEN_DELAY = 4 * 1000; // 4 seconds
var ACTION_TIMEOUT = 40 * 1000; // 40 seconds
var DISCONNECTED_EVENTS = [
	"Never connected to device",
	"Device isn't connected",
	"Device is disconnected",
	"Device not found"
];

module.exports = get;

function get() {
	var connected = flyd.stream();
	var disconnected = flyd.stream();
	var scanning = flyd.stream();
	var scanned = flyd.stream();
	var events = flyd.stream();
	var errors = flyd.stream();

	var scannedMap = {};
	var connectedIds = {};
	var pollTimers = {};
	var destroyed = false;
	var isConnecting = false;

	disconnected.map(closeDevice);

	var api = {
		type: "bluetoothle",

		// Streams of useful events
		connected: connected,
		disconnected: disconnected,
		scanning: scanning,
		scanned: scanned,
		events: events,
		errors: errors,

		// General Methods
		on: on,
		destroy: destroy,

		// Bluetooth Methods
		connect: connect,
		disconnect: disconnect,
		scan: scan,
		stopScan: stopScan,
		listen: listen,
		poll: poll,
		read: read,
		write: write,
		unlisten: unlisten,
		unpoll: unpoll,
		enable: enable,
		disable: disable,

		// Method generation
		writer: makeWriter,
		reader: makeReader,
		listener: makeListener,
		poller: makePoller
	};

	return ensureInitialized()
		.then(ensureEnabled)
		.then(function () {
			return api;
		});

	function ensureInitialized() {

		return new Promise(function (resolve, reject) {
			window.bluetoothle.isInitialized(function (data) {
				if (data === "Class not found")
					return reject("bluetoothle not available");
				if (data && data.isInitialized)
					return resolve();
				window.bluetoothle.initialize(resolve, reject);
			});
		});
	}

	function ensureEnabled() {
		return new Promise(function (resolve, reject) {
			window.bluetoothle.isEnabled(function (result) {
				if (result === "Class not found")
					return reject("bluetoothle not available");
				if (result.isEnabled) return resolve();
				window.bluetoothle.enable(resolve, reject);
			});
		});
	}

	function closeDevice(device) {
		return new Promise(function (resolve, reject) {
			window.bluetoothle.close(resolve, reject, {
				address: device
			});
		}).catch(reError("Couldn't close " + device));
	}

	function destroy() {
		if (destroyed) return;

		var start = Promise.resolve(true);
		if (scanning())
			start = stopScan();

		return start
			.then(disconnectAll)
			.then(clearAll)
			.then(endAll)
			.then(markDestroyed);
	}

	function disconnectAll() {
		return Promise.all(Object.keys(connectedIds).map(disconnect).map(noError));
	}

	function clearAll() {
		return Promise.all(values(pollTimers).map(clearInterval).map(noError));
	}

	function endAll() {
		connected.end();
		disconnected.end();
		scanning.end();
		scanned.end();
		events.end();
		errors.end();
	}

	function noError(promise) {
		return promise.catch(function(e){
			console.error(e);
		});
	}

	function markDestroyed() {
		destroyed = true;
	}

	function enable() {
		if (destroyed) return;
		return new Promise(function (resolve, reject) {
			window.bluetoothle.enable(resolve, reject);
		})
			.catch(reError("Unable to enable: Immediate failure due to Bluetooth already on or airplane mode."));
	}

	function disable() {
		if (destroyed) return;
		return new Promise(function (resolve, reject) {
			window.bluetoothle.disable(resolve, reject);
		})
			.catch(reError("Bluetooth isn't disabled, so unable to enable."));
	}

	function on(characteristic) {
		if (destroyed) return;
		return filter(isType(characteristic), events);
	}

	function scan() {
		if (destroyed) return;
		if (scanning()) return;

		scannedMap = {};

		window.bluetoothle.startScan(
			handleScanned,
			pipe(
				errors,
				par(scanning, false)
			), {} // No special paramters
		);

		function handleScanned(data) {
			if (data.status === "scanStarted") {
				scanning(true);
			} else {

				// Ensure we don't report the same device more than once
				var id = data.address;
				if (scannedMap[id]) return;
				scannedMap[id] = true;

				var advertisingData = data.advertisement;
				var advertising = [];
				if (advertisingData)
					advertising = window.bluetoothle.encodedStringToBytes(advertisingData);

				scanned({
					name: data.name,
					id: data.address,
					rssi: data.rssi,
					advertising: advertising
				});
			}
		}
	}

	function stopScan() {
		if (destroyed) return;
		if (!scanning()) return;

		return new Promise(function (resolve, reject) {
			window.bluetoothle.stopScan(
				pipe(resolve, par(scanning, false)),
				reject);
		}).catch(reError("Couldn't stop scanning"));
	}

	function connect(device) {
		if (destroyed)
			return Promise.reject(new Error("Destroyed"));
		if (connectedIds[device])
			return Promise.resolve(connectedIds[device]);

		return new Promise(function (resolve, reject) {
			if (isConnecting)
				return onFail(new Error("Already connecting to " + isConnecting));

			isConnecting = device;

			var timer = setTimeout(timedOut, ACTION_TIMEOUT);
			var hasErrored = false;

			window.bluetoothle.connect(
				onSuccess,
				onFail, {
					address: device
				});

			function onSuccess(data) {
				clearTimeout(timer);
				if (hasErrored) {
					reError("Connect " + data.status + " to device after error " + device)(data);
				}
				if (data.status === "connected") {
					isConnecting = false;
					discover(device)
						.then(par(finish, data))
						.catch(onFail);
				} else if (data.status === "disconnected") {
					handleDisconnected(device, data);
				}
			}

			function finish(data, services) {
				console.info("bluetooth: Discovered services", services);
				var normalized = {
					id: data.address,
					name: data.name
				};
				handleConnected(normalized);
				resolve(normalized);
			}

			function onFail(data) {
				isConnecting = false;
				clearTimeout(timer);
				var hasAlreadyConnected = data && data.message && (data.message.indexOf("Device previously connected") !== -1);
				// If the failure was something other than the device being disconnected
				if (hasAlreadyConnected)
					return closeDevice(device)
						.then(par(connect, device))
						.then(resolve)
						.catch(reject);

				var error = {
					device: device,
					error: data,
					message: data.message
				};

				if (hasErrored) {
					reError("Connect failed after error")(error);
				} else {
					hasErrored = true;
					reject(error);
					closeDevice(device);
				}
			}

			function timedOut() {
				onFail(new Error("Timed out"));
			}
		}).catch(reError("Couldn't connect to device " + device));
	}

	function discover(device) {
		return new Promise(function (resolve, reject) {
			window.bluetoothle.discover(resolve, reject, {
				address: device
			});
		})
			.catch(reError("Couldn't discover services for " + device));
	}

	function disconnect(device) {
		if (destroyed) return;
		if (!connectedIds[device]) return;

		return new Promise(function (resolve, reject) {
			window.bluetoothle.disconnect(
				resolve,
				reject, {
					address: device
				});
		})
			.then(par(handleDisconnected, device))
			.catch(reError("Error disconnecting " + device));
	}

	function write(service, characteristic, value, device) {
		var service_uuid = (CONSTANTS[service]);
		var characteristic_uuid = (CONSTANTS[characteristic]);
		var value_data = CONSTANTS[value] || new Uint8Array(value).buffer;
		var valueString = window.bluetoothle
			.bytesToEncodedString(new Uint8Array(value_data));

		return new Promise(function (resolve, reject) {
			var emitTimeout = par(reject, new Error("Write timed out"));
			var timer = setTimeout(emitTimeout, ACTION_TIMEOUT);

			window.bluetoothle.write(
				handle_written,
				handle_error, {
					address: device,
					service: service_uuid,
					serviceUuid: service_uuid,
					characteristic: characteristic_uuid,
					characteristicUuid: characteristic_uuid,
					value: valueString
				});

			function handle_written() {
				clearTimeout(timer);
				resolve(device);
			}

			function handle_error(error) {
				clearTimeout(timer);
				reject(error);
			}

		}).catch(handle_write_error).catch(reError("Couldn't write " + characteristic + ": " + value + " to " + device));

		function handle_write_error(error) {
			forceDisconnected(device);
			throw error;
		}
	}

	function makeWriter(service, characteristic, value) {
		return par(write, service, characteristic, value);
	}

	function read(service, characteristic, device) {
		var service_uuid = (CONSTANTS[service]);
		var characteristic_uuid = (CONSTANTS[characteristic]);

		return new Promise(function (resolve, reject) {
			var emitTimeout = par(reject, new Error("Read timed out"));
			var timer = setTimeout(emitTimeout, ACTION_TIMEOUT);

			window.bluetoothle.read(
				handle_read,
				handle_error, {
					address: device,
					service: service_uuid,
					serviceUuid: service_uuid,
					characteristic: characteristic_uuid,
					characteristicUuid: characteristic_uuid
				});

			function handle_read(data) {
				clearTimeout(timer);
				resolve(parseRead(data));
			}

			function handle_error(error) {
				clearTimeout(timer);
				reject(error);
			}
		}).catch(handle_read_error).catch(reError("Couldn't read " + characteristic + " from " + device));

		function handle_read_error(error) {
			forceDisconnected(device);
			throw error;
		}
	}

	function makeReader(service, characteristic) {
		return par(read, service, characteristic);
	}

	function listen(service, characteristic, device) {
		var service_uuid = (CONSTANTS[service]);
		var characteristic_uuid = (CONSTANTS[characteristic]);

		var errorMessage = "Couldn't listen to " + characteristic + " from " + device;

		setTimeout(function () {
			window.bluetoothle.subscribe(
				pipe(parseRead, handle),
				reError(errorMessage, true), {
					address: device,
					service: service_uuid,
					serviceUuid: service_uuid,
					characteristic: characteristic_uuid,
					characteristicUuid: characteristic_uuid,
					isNotification: true
				});
		}, LISTEN_DELAY);

		return device;

		function handle(buffer) {
			if (!buffer) return;
			var data = new Uint8Array(buffer);
			events({
				characteristic: characteristic,
				device: device,
				data: data
			});
		}
	}

	function makeListener(service, characteristic) {
		return par(listen, service, characteristic);
	}

	function poll(time, service, characteristic, device) {
		var reader = par(read, service, characteristic, device);

		var pollId = device + "#" + characteristic + "(" + (service || "") + ")";

		if (pollId in pollTimers) return device;

		pollTimers[pollId] = setInterval(reread, time);

		return device;

		function reread() {
			reader()
				.then(pushchange)
				.catch(reError("Couldn't poll " + characteristic + " from " + device));
		}

		function pushchange(buffer) {
			var data = new Uint8Array(buffer);
			events({
				characteristic: characteristic,
				device: device,
				data: data
			});
		}
	}

	function makePoller(time, characteristic, service) {
		return par(poll, time, characteristic, service);
	}

	function unlisten(service, characteristic, device) {
		var service_uuid = (CONSTANTS[service]);
		var characteristic_uuid = (CONSTANTS[characteristic]);

		return new Promise(function (resolve, reject) {
			window.bluetoothle.unsubscribe(
				resolve,
				reject, {
					address: device,
					service: service_uuid,
					serviceUuid: service_uuid,
					characteristic: characteristic_uuid,
					characteristicUuid: characteristic_uuid,
				});
		}).catch(reError("Couldn't stop listening to " + characteristic + " on " + device));
	}

	function unpoll(service, characteristic, device) {
		var pollId = device + "#" + characteristic + "(" + service + ")";

		if (!(pollId in pollTimers)) return device;

		clearInterval(pollTimers[pollId]);
		delete pollTimers[pollId];

		return device;
	}

	function forceDisconnected(device) {
		handleDisconnected(device);
		disconnect(device);
		errors(new Error("Device " + device + " disconnected"));
	}

	function handleDisconnected(device) {
		if (!connectedIds[device]) return;
		delete connectedIds[device];
		disconnected(device);
	}

	function handleConnected(device) {
		connectedIds[device.id] = device;
		connected(device);
	}

	function reError(prefix, nothrow) {
		return function (e) {
			if (e && DISCONNECTED_EVENTS.indexOf(e.message) !== -1)
				handleDisconnected(e.address, e);

			var originalMessage = (e && e.message) ? e.message : e;
			var error = new Error(prefix + ": " + originalMessage);
			error.original = e;
			errors(error);
			if (!nothrow)
				throw e;
		};
	}
}

function isType(characteristic) {
	return function (event) {
		return event.characteristic === characteristic;
	};
}

function parseRead(result) {
	if (!result.value) return;
	return window.bluetoothle.encodedStringToBytes(result.value).buffer;
}
